/**
 * Anchor-rect adjustments for steps whose DOM element is wider than what the
 * user perceives as "the field". A step opts in with `anchorRect(element,
 * rect)`; the runner then highlights and positions the bubble against the
 * returned rect instead of the element's bounding box.
 */

// Room for the caret after the last glyph so the outline never clips it.
const CARET_ALLOWANCE = 4;

let probeCanvas = null;

/** Rendered width of `text` in the element's font; `null` where canvas text metrics are unavailable (jsdom). */
const measureTextWidth = (text, style) => {
    if (typeof document === 'undefined' || typeof window.CanvasRenderingContext2D !== 'function') return null;
    try {
        probeCanvas = probeCanvas || document.createElement('canvas');
        const context = probeCanvas.getContext('2d');
        if (!context) return null;
        context.font = style.font || `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        return context.measureText(text).width;
    } catch {
        return null;
    }
};

/**
 * Shrinks a text input's rect to the text it shows (value, else placeholder),
 * keeping its left edge and height. The borderless, full-width "Task name"
 * heading input looks like a heading, so the Figma frame outlines only the
 * words and hangs the bubble under them — not under the middle of the row.
 * Falls back to the element's own rect when nothing can be measured.
 */
export const inputTextRect = (element, rect) => {
    const text = element?.value || element?.placeholder || '';
    if (!text || typeof window === 'undefined') return rect;
    const style = window.getComputedStyle(element);
    const textWidth = measureTextWidth(text, style);
    if (!textWidth) return rect;
    const horizontalInset = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth'].reduce(
        (sum, key) => sum + (parseFloat(style[key]) || 0),
        0
    );
    const width = Math.min(rect.width, Math.ceil(textWidth + horizontalInset + CARET_ALLOWANCE));
    return { ...rect, width };
};

/**
 * Highlights one descendant of the anchor instead of the anchor itself,
 * while the anchor keeps receiving `completeOnEvents`. Used where the action
 * can happen anywhere in a group but Figma points at one member of it (the
 * "Performance" card of the priority ranking, 9430-3293). Falls back to the
 * anchor's rect while the descendant is missing.
 */
export const childRect = (selector) => (element, rect) => {
    const child = element?.querySelector?.(selector);
    if (!child) return rect;
    const { top, left, width, height } = child.getBoundingClientRect();
    return { top, left, width, height };
};

const boxOf = (node) => {
    const { top, left, width, height } = node.getBoundingClientRect();
    return { top, left, width, height };
};

/** Smallest rect containing both. */
const union = (a, b) => {
    const top = Math.min(a.top, b.top);
    const left = Math.min(a.left, b.left);
    return {
        top,
        left,
        width: Math.max(a.left + a.width, b.left + b.width) - left,
        height: Math.max(a.top + a.height, b.top + b.height) - top,
    };
};

/**
 * Highlights a descendant of the anchor together with a panel that is
 * portaled *outside* it — a dropdown whose options render at document level
 * (Spark `DropdownDescriptiveMenu`, `position: fixed` under `document.body`)
 * so the anchor's own box can never include them. Figma 9455:7106 draws one
 * outline around the "Data was created by" trigger and its open options,
 * leaving the field label out. While the panel is closed (or not yet laid
 * out) the trigger alone is highlighted; without the trigger, the anchor box.
 *
 * Pair with the step's `observe: [documentSelector]` so the rect is
 * re-measured when the panel mounts, moves or unmounts.
 */
/**
 * The trigger box (`childSelector` under the anchor, else the anchor box) and
 * the open panel's box (`documentSelector` anywhere in the document), or
 * `null` for the panel while it is closed, unmounted or laid out empty.
 */
const triggerAndPanel = (childSelector, documentSelector) => (element, rect) => {
    const child = element?.querySelector?.(childSelector);
    const base = child ? boxOf(child) : rect;
    if (typeof document === 'undefined') return { base, panel: null };
    let panel = null;
    try {
        panel = document.querySelector(documentSelector);
    } catch {
        panel = null;
    }
    if (!panel) return { base, panel: null };
    const panelBox = boxOf(panel);
    return { base, panel: panelBox.width > 0 && panelBox.height > 0 ? panelBox : null };
};

export const unionRects = (childSelector, documentSelector) => {
    const measure = triggerAndPanel(childSelector, documentSelector);
    return (element, rect) => {
        const { base, panel } = measure(element, rect);
        return panel ? union(base, panel) : base;
    };
};

/**
 * `unionRects` whose open-panel rect also carries `parts: [trigger, panel]`,
 * so the coach mark can draw one outline that hugs both boxes (the panel
 * hanging off the trigger) instead of their plain bounding box.
 */
export const notchedUnionRects = (childSelector, documentSelector) => {
    const measure = triggerAndPanel(childSelector, documentSelector);
    return (element, rect) => {
        const { base, panel } = measure(element, rect);
        return panel ? { ...union(base, panel), parts: [base, panel] } : base;
    };
};
